import { Injectable, OnDestroy } from "@angular/core";
import { EventTypes, OidcSecurityService, PublicEventsService } from "angular-auth-oidc-client";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { PurchaseOrderActions } from "../store/actions/purchase-order.actions";
import { AppState } from "../store/reducers";
import { AppConfigService } from "./app.config.service";

@Injectable({ providedIn: "root" })
export class OrderStatusWebSocketService implements OnDestroy {
	private static readonly BASE_RECONNECT_DELAY_MS = 1000;
	private static readonly MAX_RECONNECT_DELAY_MS = 30000;

	private socket?: WebSocket;
	private intentionalClose = false;
	private isOpening = false;
	private reconnectAttempts = 0;
	private reconnectTimer?: ReturnType<typeof setTimeout>;
	private authEventsSub?: Subscription;

	constructor(
		private store: Store<AppState>,
		private appConfigService: AppConfigService,
		private oidcSecurityService: OidcSecurityService,
		private publicEvents: PublicEventsService
	) {
		window.addEventListener("online", this.handleResume);
		document.addEventListener("visibilitychange", this.handleVisibilityChange);
		// An expired access token makes the upgrade 401 and the socket goes quiet until the
		// backoff happens to fire after a renewal — up to 30s of a silently stale page.
		// The library owns renewal (forcing it here would race its silent-renew loop, see
		// AuthEventsService), so react to it: a new token means retry the upgrade now.
		// The browser hides the upgrade status, so a rejected token is indistinguishable
		// from a network drop — hence reconnect-on-renewal rather than suppress-on-401.
		this.authEventsSub = this.publicEvents
			.registerForEvents()
			.pipe(filter((event) => event.type === EventTypes.NewAuthenticationResult))
			.subscribe(() => {
				if (this.intentionalClose || this.socket) {
					return;
				}
				this.reconnectAttempts = 0;
				this.clearReconnectTimer();
				this.open();
			});
	}

	connect(): void {
		this.intentionalClose = false;
		this.open();
	}

	ngOnDestroy(): void {
		window.removeEventListener("online", this.handleResume);
		document.removeEventListener("visibilitychange", this.handleVisibilityChange);
		this.authEventsSub?.unsubscribe();
		this.disconnect();
	}

	disconnect(): void {
		this.intentionalClose = true;
		this.isOpening = false;
		this.clearReconnectTimer();
		this.reconnectAttempts = 0;
		if (this.socket) {
			try {
				this.socket.close();
			} catch {
				// Closing a socket should never throw into callers.
			}
			this.socket = undefined;
		}
	}

	private handleResume = (): void => {
		if (this.intentionalClose) {
			return;
		}
		this.open();
		this.store.dispatch(PurchaseOrderActions.selectedOrder.reconcile());
	};

	private handleVisibilityChange = (): void => {
		if (document.visibilityState === "visible") {
			this.handleResume();
		}
	};

	private open(): void {
		if (!this.isEnabled() || this.intentionalClose || this.isOpening) {
			return;
		}
		if (
			this.socket &&
			(this.socket.readyState === WebSocket.OPEN ||
				this.socket.readyState === WebSocket.CONNECTING)
		) {
			return;
		}

		this.isOpening = true;
		this.oidcSecurityService
			.getAccessToken()
			.pipe(take(1))
			.subscribe({
				next: (token) => {
					this.isOpening = false;
					if (!token || this.intentionalClose) {
						return;
					}
					let socket: WebSocket;
					try {
						socket = new WebSocket(this.buildUrl(), this.buildProtocols(token));
					} catch {
						this.scheduleReconnect();
						return;
					}
					this.socket = socket;

					socket.onopen = () => {
						this.reconnectAttempts = 0;
						this.store.dispatch(PurchaseOrderActions.selectedOrder.reconcile());
					};
					socket.onmessage = (event) => this.handleMessage(event);
					socket.onerror = () => {
					};
					socket.onclose = () => {
						if (this.socket === socket) {
							this.socket = undefined;
						}
						if (!this.intentionalClose) {
							this.scheduleReconnect();
						}
					};
				},
				error: () => {
					this.isOpening = false;
					this.scheduleReconnect();
				}
			});
	}

	private handleMessage(event: MessageEvent): void {
		let payload: { type?: string; orderId?: number; lineId?: number; status?: string };
		try {
			payload = JSON.parse(event.data);
		} catch {
			return;
		}
		if (payload?.type === "ORDER_STATUS" && payload.orderId != null && payload.lineId != null && payload.status) {
			this.store.dispatch(
				PurchaseOrderActions.selectedOrder.wsStatusReceived({
					orderId: payload.orderId,
					lineId: payload.lineId,
					status: payload.status
				})
			);
		}
	}

	private scheduleReconnect(): void {
		if (this.intentionalClose) {
			return;
		}
		this.clearReconnectTimer();
		const delay = Math.min(
			OrderStatusWebSocketService.BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts,
			OrderStatusWebSocketService.MAX_RECONNECT_DELAY_MS
		);
		this.reconnectAttempts += 1;
		this.reconnectTimer = setTimeout(() => this.open(), delay);
	}

	private clearReconnectTimer(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = undefined;
		}
	}

	private isEnabled(): boolean {
		return this.appConfigService.getConfigValue("ORDER_STATUS_WS_ENABLED") === true;
	}

	private buildUrl(): string {
		const apiEndpoint: string = this.appConfigService.getConfigValue("API_ENDPOINT") || "";
		const base = new URL(apiEndpoint, window.location.origin);
		const wsProtocol = base.protocol === "https:" ? "wss:" : "ws:";
		return `${wsProtocol}//${base.host}/ws/order-status`;
	}

	private buildProtocols(accessToken: string): string[] {
		const authProtocol = encodeURIComponent(
			`quarkus-http-upgrade#Authorization#Bearer ${accessToken}`
		);
		return ["bearer-token-carrier", authProtocol];
	}
}
